import React, { useCallback, useEffect, useState } from "react";
import "./admin.css";
import ScoresAdmin from "./ScoresAdmin";
import ModeConfigAdmin from "./ModeConfigAdmin";

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "אירעה שגיאה, נסה שוב";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function fmtDur(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  if (s < 60) return `${s} שנ׳`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}:${String(s % 60).padStart(2, "0")} דק׳`;
  const h = Math.floor(m / 60);
  return `${h}:${String(m % 60).padStart(2, "0")} שע׳`;
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
}

const AdminLogin = ({ onSuccess }) => {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async () => {
    const password = pw.trim();
    if (!password) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(formatApiErrorDetail(body.detail));
        return;
      }
      onSuccess();
    } catch (e) {
      console.warn("admin login failed", e);
      setError("שגיאת תקשורת עם השרת");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login" data-testid="admin-login-form">
      <div className="admin-label">הכנס סיסמת מנהל:</div>
      <input
        className="name-input admin-pw"
        data-testid="admin-password-input"
        type="password"
        inputMode="numeric"
        value={pw}
        autoFocus
        disabled={loading}
        onChange={(e) => {
          setPw(e.target.value);
          setError("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") login();
        }}
        placeholder="סיסמה"
      />
      {error && (
        <div className="admin-error" data-testid="admin-login-error">
          {error}
        </div>
      )}
      <button className="rbtn primary" data-testid="admin-login-btn" onClick={login} disabled={loading}>
        {loading ? "...מתחבר" : "כניסה"}
      </button>
    </div>
  );
};

const LogsTable = ({ sessions }) => (
  <div className="admin-table-wrap">
    <table className="admin-table" data-testid="admin-logs-table">
      <thead>
        <tr>
          <th>שם</th>
          <th>מכשיר</th>
          <th>IP</th>
          <th>כניסה</th>
          <th>משך</th>
          <th>משחקים</th>
          <th>שיא</th>
        </tr>
      </thead>
      <tbody>
        {sessions.map((s) => (
          <tr key={s.id} data-testid="admin-log-row">
            <td className="cell-name">{s.name}</td>
            <td className="cell-dev">
              {s.device}
              {s.screen ? <div className="cell-scr">{s.screen}</div> : null}
            </td>
            <td className="cell-ip" data-testid="cell-ip">{s.ip || "−"}</td>
            <td>{fmtTime(s.started_at)}</td>
            <td>{fmtDur(s.duration_seconds)}</td>
            <td className="cell-num">{s.games_played}</td>
            <td className="cell-num">{s.best_score > 0 ? Math.round(s.best_score).toLocaleString("en-US") : "−"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Admin({ onBack }) {
  // null = checking the httpOnly-cookie session, false = login needed
  const [authed, setAuthed] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("logs"); // "logs" | "scores" | "mode"

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/logs?limit=200`, { credentials: "include" });
      if (res.status === 401) {
        setAuthed(false);
        setData(null);
        return;
      }
      if (!res.ok) throw new Error(`status ${res.status}`);
      setData(await res.json());
    } catch (e) {
      console.warn("failed to fetch admin logs", e);
      setError("שגיאה בטעינת הנתונים");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
        setAuthed(res.ok);
      } catch (e) {
        console.warn("session check failed", e);
        setAuthed(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (authed) fetchLogs();
  }, [authed, fetchLogs]);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    } catch (e) {
      console.warn("logout request failed", e);
    }
    setAuthed(false);
    setData(null);
  }, []);

  const stats = data && data.stats;
  const sessions = (data && data.sessions) || [];

  return (
    <div className="admin-panel" dir="rtl" data-testid="admin-panel">
      <div className="admin-head">
        <button className="rbtn small" data-testid="admin-back-btn" onClick={onBack}>
          → חזרה למשחק
        </button>
        <div className="admin-title">מסך ניהול</div>
        {authed ? (
          <button className="rbtn small" data-testid="admin-logout-btn" onClick={logout}>
            התנתק
          </button>
        ) : (
          <span className="admin-head-spacer" />
        )}
      </div>

      {authed === null && (
        <div className="admin-empty" data-testid="admin-checking">
          ...בודק הרשאות
        </div>
      )}

      {authed === false && <AdminLogin onSuccess={() => setAuthed(true)} />}

      {authed && (
        <div className="admin-body">
          <div className="admin-tabs" data-testid="admin-tabs">
            <button
              className={"admin-tab" + (tab === "logs" ? " active" : "")}
              data-testid="tab-logs"
              onClick={() => setTab("logs")}
            >
              יומן כניסות
            </button>
            <button
              className={"admin-tab" + (tab === "scores" ? " active" : "")}
              data-testid="tab-scores"
              onClick={() => setTab("scores")}
            >
              טבלת שיאים
            </button>
            <button
              className={"admin-tab" + (tab === "mode" ? " active" : "")}
              data-testid="tab-mode"
              onClick={() => setTab("mode")}
            >
              כללי מצב
            </button>
          </div>

          {tab === "logs" && (
            <>
              {stats && (
                <div className="admin-stats">
                  <div className="admin-stat" data-testid="stat-total-sessions">
                    <span>{stats.total_sessions}</span>כניסות
                  </div>
                  <div className="admin-stat" data-testid="stat-unique-players">
                    <span>{stats.unique_players}</span>שחקנים
                  </div>
                  <div className="admin-stat" data-testid="stat-total-time">
                    <span>{fmtDur(stats.total_seconds)}</span>זמן כולל
                  </div>
                </div>
              )}
              <div className="admin-toolbar">
                <div className="admin-sub">יומן כניסות למערכת</div>
                <button className="rbtn small" data-testid="admin-refresh-btn" onClick={fetchLogs} disabled={loading}>
                  {loading ? "...טוען" : "רענון ⟳"}
                </button>
              </div>
              {error && <div className="admin-error">{error}</div>}
              {!data && loading ? (
                <div className="admin-empty" data-testid="admin-loading">
                  ...טוען נתונים
                </div>
              ) : sessions.length === 0 ? (
                <div className="admin-empty" data-testid="admin-empty">
                  אין עדיין כניסות רשומות
                </div>
              ) : (
                <LogsTable sessions={sessions} />
              )}
            </>
          )}

          {tab === "scores" && <ScoresAdmin />}

          {tab === "mode" && <ModeConfigAdmin />}
        </div>
      )}
    </div>
  );
}
